import React, { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { MdClose } from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";

import {
    ActionSection,
    FieldWrapper,
    InputGroup,
    Label,
    ModalHeader,
    ScrollableContent,
    SideCancelBtn,
    SubmitBtn,
    Wrapper,
} from "../Styles/InputStyles";

import {
    FormControl,
    MenuItem,
    Select,
} from "@mui/material";

const generateTimeOptions = (
    startHour,
    endHour,
    interval = 30
) => {

    const options = [];

    for (let h = startHour; h <= endHour; h++) {

        for (let m = 0; m < 60; m += interval) {

            if (h === endHour && m > 0) continue;

            const hour12 = h % 12 || 12;
            const ampm = h >= 12 ? "PM" : "AM";

            options.push({
                label: `${hour12}:${m
                    .toString()
                    .padStart(2, "0")} ${ampm}`,
                value: `${h
                    .toString()
                    .padStart(2, "0")}:${m
                        .toString()
                        .padStart(2, "0")}`,
            });
        }
    }

    return options;
};

const TIME_OPTIONS = {
    breakfast: {
        start: generateTimeOptions(6, 10),
        end: generateTimeOptions(7, 11),
    },

    lunch: {
        start: generateTimeOptions(12, 14),
        end: generateTimeOptions(13, 16),
    },

    snacks: {
        start: generateTimeOptions(15, 17),
        end: generateTimeOptions(16, 18),
    },

    dinner: {
        start: generateTimeOptions(18, 21),
        end: generateTimeOptions(19, 23),
    },
};

const addMinutes = (time, minutes) => {
    const [hour, minute] = time.split(":").map(Number);

    const date = new Date();
    date.setHours(hour);
    date.setMinutes(minute + minutes);

    return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`;
};

const getTimeValue = (date) => {

    if (!date) return "";

    return `${date
        .getHours()
        .toString()
        .padStart(2, "0")}:${date
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;

};

const getDateFromTime = (time) => {

    if (!time) return null;

    const [hours, minutes] = time.split(":");

    const date = new Date();

    date.setHours(Number(hours));
    date.setMinutes(Number(minutes));
    date.setSeconds(0);

    return date;
};

const AddFoodMenu = ({
    handleClose,
    selectedData,
    addMenuLoading,
    addMenuMutate,
    propId,
    editMenuLoading,
    editMenuMutate,
}) => {

    /*********************************************************
     * Refs
     *********************************************************/

    const breakfastRef = useRef(null);
    const lunchRef = useRef(null);
    const snacksRef = useRef(null);
    const dinnerRef = useRef(null);

    const [activeMeal, setActiveMeal] = useState("breakfast");

    /*********************************************************
     * Meal Tabs
     *********************************************************/

    const mealTabs = [
        {
            key: "breakfast",
            label: "Breakfast",
            ref: breakfastRef,
        },
        {
            key: "lunch",
            label: "Lunch",
            ref: lunchRef,
        },
        {
            key: "snacks",
            label: "Snacks",
            ref: snacksRef,
        },
        {
            key: "dinner",
            label: "Dinner",
            ref: dinnerRef,
        },
    ];

    /*********************************************************
     * Observe Visible Meal
     *********************************************************/

    useEffect(() => {

        const sections = [
            { key: "breakfast", ref: breakfastRef },
            { key: "lunch", ref: lunchRef },
            { key: "snacks", ref: snacksRef },
            { key: "dinner", ref: dinnerRef },
        ];

        const observer = new IntersectionObserver(
            (entries) => {

                entries.forEach((entry) => {

                    if (entry.isIntersecting) {
                        const meal = sections.find(
                            item => item.ref.current === entry.target
                        );

                        if (meal) {
                            setActiveMeal(meal.key);
                        }
                    }

                });

            },
            {
                threshold: 0.35,
            }
        );

        sections.forEach(section => {
            if (section.ref.current) {
                observer.observe(section.ref.current);
            }
        });

        return () => observer.disconnect();

    }, []);

    /*********************************************************
     * Helpers
     *********************************************************/

    const parseTimeString = (timeStr) => {

        if (!timeStr) return null;

        if (
            timeStr.toLowerCase().includes("am") ||
            timeStr.toLowerCase().includes("pm")
        ) {

            const timePart = timeStr.split(" ")[0];
            const period = timeStr.split(" ")[1].toLowerCase();

            let [hours, minutes] = timePart.split(":").map(Number);

            if (period === "pm" && hours !== 12) {
                hours += 12;
            } else if (period === "am" && hours === 12) {
                hours = 0;
            }

            const date = new Date();
            date.setHours(hours, minutes);

            return date;
        }

        const [hours, minutes] = timeStr.split(":").map(Number);

        const date = new Date();

        date.setHours(hours, minutes);

        return date;
    };

    const formatTimeForBackend = (date) => {
        if (!date) return "";
        return `${date
            .getHours()
            .toString()
            .padStart(2, "0")}:${date
                .getMinutes()
                .toString()
                .padStart(2, "0")}`;

    };

    const initialDayOfWeek =
        selectedData?.dayOfWeek ||
        new Date().getDay() ||
        7;

    const initialValues = {
        dayOfWeek: initialDayOfWeek,

        breakfast: {
            startTime: parseTimeString(
                selectedData?.breakfastTiming?.split(" - ")[0]
            ),
            endTime: parseTimeString(
                selectedData?.breakfastTiming?.split(" - ")[1]
            ),
            details: selectedData?.breakfast || "",
        },

        lunch: {
            startTime: parseTimeString(
                selectedData?.lunchTiming?.split(" - ")[0]
            ),
            endTime: parseTimeString(
                selectedData?.lunchTiming?.split(" - ")[1]
            ),
            vegDetails: selectedData?.lunch || "",
            nonVegDetails: selectedData?.lunchNonVeg || "",
        },

        snacks: {
            startTime: parseTimeString(
                selectedData?.snackTiming?.split(" - ")[0]
            ),
            endTime: parseTimeString(
                selectedData?.snackTiming?.split(" - ")[1]
            ),
            details: selectedData?.snack || "",
        },

        dinner: {
            startTime: parseTimeString(
                selectedData?.dinnerTiming?.split(" - ")[0]
            ),
            endTime: parseTimeString(
                selectedData?.dinnerTiming?.split(" - ")[1]
            ),
            vegDetails: selectedData?.dinner || "",
            nonVegDetails: selectedData?.dinnerNonVeg || "",
        },
    };

    // Keep your existing handleSubmit here...
    const handleSubmit = (values) => {
        const hasAtLeastOneMeal = ['breakfast', 'lunch', 'snacks', 'dinner'].some(meal => {
            if (meal === 'lunch') {
                return values[meal].startTime && values[meal].endTime &&
                    (values[meal].vegDetails || values[meal].nonVegDetails);
            } else if (meal === 'dinner') {
                return values[meal].startTime && values[meal].endTime &&
                    (values[meal].vegDetails || values[meal].nonVegDetails);
            } else {
                return values[meal].details && values[meal].startTime && values[meal].endTime;
            }
        });

        if (!hasAtLeastOneMeal) {
            toast.error("Please fill at least one complete meal section");
            return;
        }

        const hasPartialMeals = ['breakfast', 'lunch', 'snacks', 'dinner'].some(meal => {
            if (meal === 'lunch') {
                const section = values[meal];
                const hasAnyDetail = section.vegDetails || section.nonVegDetails;
                return (
                    (hasAnyDetail || section.startTime || section.endTime) &&
                    !(section.startTime && section.endTime && hasAnyDetail)
                );
            } else if (meal === 'dinner') {
                const section = values[meal];
                const hasAnyDetail = section.vegDetails || section.nonVegDetails;
                return (
                    (hasAnyDetail || section.startTime || section.endTime) &&
                    !(section.startTime && section.endTime && hasAnyDetail)
                );
            } else {
                const section = values[meal];
                return (
                    (section.details || section.startTime || section.endTime) &&
                    !(section.details && section.startTime && section.endTime)
                );
            }
        });

        if (hasPartialMeals) {
            toast.error("Please complete all fields for any meal section you start, or leave it empty");
            return;
        }

        const payload = {
            propId: propId,
            dayOfWeek: initialDayOfWeek,
            breakfast: values.breakfast.details,
            breakfastTiming: values.breakfast.details
                ? `${formatTimeForBackend(values.breakfast.startTime)} - ${formatTimeForBackend(values.breakfast.endTime)}`
                : '',
            lunch: values.lunch.vegDetails,
            lunchNonVeg: values.lunch.nonVegDetails,
            lunchTiming: (values.lunch.vegDetails || values.lunch.nonVegDetails)
                ? `${formatTimeForBackend(values.lunch.startTime)} - ${formatTimeForBackend(values.lunch.endTime)}`
                : '',
            snacks: values.snacks.details,
            snacksTiming: values.snacks.details
                ? `${formatTimeForBackend(values.snacks.startTime)} - ${formatTimeForBackend(values.snacks.endTime)}`
                : '',
            dinner: values.dinner.vegDetails,
            dinnerNonVeg: values.dinner.nonVegDetails,
            dinnerTiming: (values.dinner.vegDetails || values.dinner.nonVegDetails)
                ? `${formatTimeForBackend(values.dinner.startTime)} - ${formatTimeForBackend(values.dinner.endTime)}`
                : '',
            ...(selectedData.id && { menuId: selectedData.id })
        };

        if (selectedData.id) {
            editMenuMutate(payload);
        } else {
            addMenuMutate(payload);
        }
    }

    const scrollToSection = (ref) => {
        if (!ref.current) return;

        const scrollContainer = ref.current.closest(".custom-scrollbar");

        if (!scrollContainer) return;

        const stickyHeight = 90; // Tabs height

        const top =
            ref.current.offsetTop - stickyHeight;

        scrollContainer.scrollTo({
            top,
            behavior: "smooth",
        });
    };

    return (
        <Container>
            <ModalHeader>
                <Title>
                    {selectedData.id ? "Edit Menu" : "Add Menu"}
                </Title>

                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="h-full flex flex-col">
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue }) => (
                        <Form className="flex h-full flex-col">
                            {/* Sticky Navigation */}
                            <div className="px-6 sticky top-0 z-30 bg-white py-4 border-b border-gray-200">

                                <div className="flex gap-3 overflow-x-auto scrollbar-hide">
                                    {mealTabs.map(tab => (
                                        <button
                                            key={tab.key}
                                            type="button"
                                            onClick={() => scrollToSection(tab.ref)}
                                            className={`px-5 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all
                                            ${activeMeal === tab.key
                                                    ? "bg-gradient-to-r from-primary-color to-[#00A0FF] text-white shadow"
                                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                }`}
                                        >
                                            {tab.label}
                                        </button>

                                    ))}

                                </div>

                            </div>

                            <ScrollableContent className="px-6 max-h-[calc(100vh-190px)]">
                                <Wrapper>
                                    {/* PART-2 STARTS HERE */}

                                    {/* Breakfast Section */}
                                    <div ref={breakfastRef} className="mt-2 scroll-mt-28">
                                        <MealSection>
                                            <div className="flex items-center justify-between mb-0">
                                                <MealTitle>Breakfast</MealTitle>
                                                {(values.breakfast.details &&
                                                    values.breakfast.startTime &&
                                                    values.breakfast.endTime) && (
                                                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                                            Complete
                                                        </span>
                                                    )}
                                            </div>

                                            <MealTimingSelector
                                                meal="breakfast"
                                                values={values.breakfast}
                                                setFieldValue={setFieldValue}
                                            />

                                            <InputGroup>
                                                <Label>Breakfast Menu</Label>
                                                <FieldWrapper>
                                                    <Field
                                                        as="textarea"
                                                        name="breakfast.details"
                                                        placeholder="Poha, Sandwich, Tea..."
                                                        className="details-input min-h-[180px]"
                                                    />
                                                </FieldWrapper>
                                                <FieldHelperText />
                                            </InputGroup>
                                        </MealSection>
                                    </div>

                                    {/* ======= Lunch Section ====== */}

                                    <div ref={lunchRef} className="mt-2 scroll-mt-28">
                                        <MealSection>
                                            <div className="flex items-center justify-between mb-4">
                                                <MealTitle>Lunch</MealTitle>
                                                {(
                                                    (values.lunch.vegDetails ||
                                                        values.lunch.nonVegDetails) &&
                                                    values.lunch.startTime &&
                                                    values.lunch.endTime
                                                ) && (
                                                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                                            Complete
                                                        </span>
                                                    )}
                                            </div>

                                            <MealTimingSelector
                                                meal="lunch"
                                                values={values.lunch}
                                                setFieldValue={setFieldValue}
                                            />

                                            <div className="grid gap-5 mt-5">
                                                <div className="rounded-xl border border-green-200 bg-green-50 p-5">

                                                    <div className="flex items-center gap-2 mb-3">

                                                        <span className="w-3 h-3 rounded-full bg-green-500" />

                                                        <Label className="!mb-0 text-green-700">
                                                            Veg Menu
                                                        </Label>

                                                    </div>

                                                    <FieldWrapper>

                                                        <Field
                                                            as="textarea"
                                                            name="lunch.vegDetails"
                                                            placeholder="Dal, Rice, Paneer..."
                                                            className="details-input min-h-[180px]"
                                                        />
                                                    </FieldWrapper>
                                                    <FieldHelperText text={"Enter multiple items separated by commas e.g. Rice, Dal, Paneer Curry."} />
                                                </div>

                                                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className="w-3 h-3 rounded-full bg-red-500" />
                                                        <Label className="!mb-0 text-red-700">
                                                            Non-Veg Menu
                                                        </Label>
                                                    </div>

                                                    <FieldWrapper>
                                                        <Field
                                                            as="textarea"
                                                            name="lunch.nonVegDetails"
                                                            placeholder="Chicken Curry..."
                                                            className="details-input min-h-[180px]"
                                                        />
                                                    </FieldWrapper>
                                                    <FieldHelperText text={"Enter multiple items separated by commas e.g. Rice, Dal, Paneer Curry."} />
                                                </div>

                                            </div>
                                        </MealSection>
                                    </div>

                                    {/* ====== Snacks Section ====== */}

                                    <div ref={snacksRef} className="mt-2 scroll-mt-28">
                                        <MealSection>
                                            <div className="flex items-center justify-between mb-4">
                                                <MealTitle>Snacks</MealTitle>
                                                {(values.snacks.details &&
                                                    values.snacks.startTime &&
                                                    values.snacks.endTime) && (
                                                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                                            Complete
                                                        </span>
                                                    )}
                                            </div>

                                            <MealTimingSelector
                                                meal="snacks"
                                                values={values.snacks}
                                                setFieldValue={setFieldValue}
                                            />

                                            <InputGroup>
                                                <Label>Snacks Menu</Label>
                                                <FieldWrapper>
                                                    <Field
                                                        as="textarea"
                                                        name="snacks.details"
                                                        placeholder="Tea, Coffee, Biscuits..."
                                                        className="details-input min-h-[180px]"
                                                    />
                                                </FieldWrapper>
                                                <FieldHelperText />
                                            </InputGroup>
                                        </MealSection>
                                    </div>

                                    {/* ====== Dinner Section ===== */}

                                    <div ref={dinnerRef} className="mt-2 scroll-mt-28">
                                        <MealSection>
                                            <div className="flex items-center justify-between mb-4">
                                                <MealTitle>Dinner</MealTitle>
                                                {(
                                                    (values.dinner.vegDetails ||
                                                        values.dinner.nonVegDetails) &&
                                                    values.dinner.startTime &&
                                                    values.dinner.endTime
                                                ) && (
                                                        <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                                                            Complete
                                                        </span>
                                                    )}
                                            </div>

                                            <MealTimingSelector
                                                meal="dinner"
                                                values={values.dinner}
                                                setFieldValue={setFieldValue}
                                            />

                                            <div className="grid gap-5 mt-5">
                                                <div className="rounded-xl border border-green-200 bg-green-50 p-5">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className="w-3 h-3 rounded-full bg-green-500" />
                                                        <Label className="!mb-0 text-green-700">
                                                            Veg Menu
                                                        </Label>
                                                    </div>

                                                    <FieldWrapper>
                                                        <Field
                                                            as="textarea"
                                                            name="dinner.vegDetails"
                                                            placeholder="Dal, Chapati..."
                                                            className="details-input min-h-[180px]"
                                                        />
                                                    </FieldWrapper>
                                                    <FieldHelperText text={"Enter multiple items separated by commas e.g. Rice, Dal, Paneer Curry."} />
                                                </div>

                                                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <span className="w-3 h-3 rounded-full bg-red-500" />
                                                        <Label className="!mb-0 text-red-700">
                                                            Non-Veg Menu
                                                        </Label>
                                                    </div>
                                                    <FieldWrapper>
                                                        <Field
                                                            as="textarea"
                                                            name="dinner.nonVegDetails"
                                                            placeholder="Chicken, Egg Curry..."
                                                            className="details-input min-h-[180px]"
                                                        />
                                                    </FieldWrapper>
                                                    <FieldHelperText text={"Enter multiple items separated by commas e.g. Rice, Dal, Paneer Curry."} />
                                                </div>
                                            </div>
                                        </MealSection>
                                    </div>
                                </Wrapper>
                            </ScrollableContent>

                            {/* ====== Action Buttons ======= */}

                            <ActionSection className="px-4 sticky bottom-0 bg-white border-t border-gray-100 py-5 mt-8">

                                <SideCancelBtn
                                    type="button"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </SideCancelBtn>

                                <SubmitBtn
                                    type="submit"
                                    disabled={
                                        selectedData.id
                                            ? editMenuLoading
                                            : addMenuLoading
                                    }
                                >
                                    {selectedData.id
                                        ? editMenuLoading
                                            ? "Updating..."
                                            : "Update Menu"
                                        : addMenuLoading
                                            ? "Saving..."
                                            : "Save Menu"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const MIN_MEAL_DURATION = {
    breakfast: 30,
    lunch: 30,
    snacks: 30,
    dinner: 30,
};

const MealTimingSelector = ({
    meal,
    values,
    setFieldValue,
}) => {
    const startTimeValue = getTimeValue(values.startTime);
    const endTimeValue = getTimeValue(values.endTime);
    const minimumEndTime = startTimeValue
        ? addMinutes(
            startTimeValue,
            MIN_MEAL_DURATION[meal]
        )
        : "";
    const endOptions = TIME_OPTIONS[meal].end.filter(
        option => option.value >= minimumEndTime
    );

    return (
        // <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
        <div className="space-y-4">
            <div className="!mb-0">
                Meal Timing
            </div>

            <div className="grid grid-cols-2 gap-4">
                {/* Start Time */}
                <div>
                    <Label>Start Time</Label>
                    <FormControl
                        fullWidth
                        size="small"
                    >
                        <Select
                            value={startTimeValue}
                            displayEmpty
                            onChange={(e) => {
                                const newStartTime = e.target.value;
                                setFieldValue(
                                    `${meal}.startTime`,
                                    getDateFromTime(newStartTime)
                                );

                                const minimumEnd = addMinutes(
                                    newStartTime,
                                    MIN_MEAL_DURATION[meal]
                                );

                                const nextValidEnd =
                                    TIME_OPTIONS[meal].end.find(
                                        option =>
                                            option.value >= minimumEnd
                                    );

                                if (
                                    !endTimeValue ||
                                    endTimeValue < minimumEnd
                                ) {
                                    setFieldValue(
                                        `${meal}.endTime`,
                                        getDateFromTime(
                                            nextValidEnd.value
                                        )
                                    );
                                }
                            }}
                        >
                            {TIME_OPTIONS[meal].start.map(option => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                {/* End Time */}
                <div>
                    <Label>End Time</Label>
                    <FormControl
                        fullWidth
                        size="small"
                    >
                        <Select
                            value={
                                endOptions.some(
                                    x => x.value === endTimeValue
                                )
                                    ? endTimeValue
                                    : ""
                            }
                            displayEmpty
                            onChange={(e) =>
                                setFieldValue(
                                    `${meal}.endTime`,
                                    getDateFromTime(
                                        e.target.value
                                    )
                                )
                            }
                        >
                            {endOptions.map(option => (

                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            </div>
        </div>
    );
};

const FieldHelperText = ({
    text = 'Enter multiple items separated by commas (e.g. Poha, Idli, Tea)',
}) => {
    return (
        <Text>
            {text}
        </Text>
    );
};

const Text = tw.p`text-gray-500 text-xs font-normal mt-1`;

/* ======================================
    Styled Components
====================================== */

const Container = tw.div`
bg-white
w-full
h-full
flex
flex-col
`;

const Title = tw.h1`
text-white
text-lg
font-semibold
`;

const MealSection = tw.div`
bg-white
rounded-xl
border
border-gray-200
shadow-sm
hover:shadow-md
transition-all
duration-300
p-4
space-y-5
`;

const MealTitle = tw.h2`
text-lg
font-semibold
text-primary-color
`;

export default AddFoodMenu;